import type { CameraMode } from "@uzverse/core";
import { useEffect, useMemo, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useSyncExternalStore } from "react";
import { blueprint } from "../blueprint/instance.js";
import { isLandmark } from "../bounds.js";
import { useWorldT } from "../i18n.js";
import { PerfOverlay } from "../perf/PerfOverlay.js";
import { isCoarseDevice } from "../perf/quality.js";
import { Icon } from "@uzverse/ui";
import { layoutStore } from "../layout.js";
import { useWorldState } from "../store.js";
import type { WorldStore } from "../store.js";
import { ElevatorHud } from "../elevator/ElevatorHud.js";
import { elevators } from "../elevator/store.js";
import { FloorNav } from "./FloorNav.js";
import { Compass, Crosshair, GameCorner, Minimap, hudIdleMs, useIdle } from "./GameHud.js";
import { hudUi, useHudUi } from "./hudUi.js";
import { SettingsSheet } from "./SettingsSheet.js";
import { vehicleStore } from "../vehicles/store.js";
import { MODE_KEYS, isLiveMode } from "./modes.js";
import { locoStatus } from "../loco/status.js";
import { MOVE_KEYS, isTypingTarget, moveInput } from "./input.js";
import { navStore, useNavState } from "./nav.js";
import type { NavStore } from "./nav.js";
import "./nav.css";
import "./hud.css";

const ICONS = {
  orbit: "globe",
  first_person: "eye",
  third_person: "user",
  fly: "plane",
  blueprint: "grid",
} as const;

/** Esc opens the settings only when nothing else (a drawer, the dealership, the dock) is using it. */
function escOpensSettings(s: { mode: CameraMode; locked: boolean }): boolean {
  if (!isLiveMode(s.mode) || s.locked) return false;
  const l = layoutStore.getState();
  if (l.panelOpen || l.navOpen) return false;
  const v = vehicleStore.getState();
  if (v.galeri.open || v.topUp.open || v.drivingId) return false;
  const a = document.activeElement;
  if (a && a !== document.body) return false;
  try {
    return globalThis.localStorage?.getItem("uz.world.dock.open") !== "1";
  } catch {
    return true;
  }
}

/** Keyboard: modes on 1-5, movement while walking or flying, E at doors, R in blueprint, ` for stats. */
function useNavKeys(store: WorldStore, nav: NavStore): void {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      // Alt is the walk modifier: it may hold a movement key, nothing else.
      if (e.altKey && !MOVE_KEYS.has(e.code)) return;
      const s = nav.getState();
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const mode = MODE_KEYS[e.key];
        if (mode && e.key.length === 1) {
          nav.setMode(mode);
          return;
        }
        if (e.code === "Backquote") {
          nav.togglePerf();
          return;
        }
        if (e.code === "KeyR" && s.mode === "blueprint" && !e.repeat) {
          blueprint.rotate(store.getState().selected?.id ?? null);
          return;
        }
        if (e.code === "KeyE" && isLiveMode(s.mode) && !e.repeat) {
          if (s.inside) {
            // A call plate or a cabin panel within reach takes the key; else it leaves the building.
            const lift = elevators.get(s.inside.id);
            if (lift?.panel) lift.closePanel();
            else if (!lift?.openPanel()) void nav.exit();
          } else if (s.nearDoor) void nav.enter(s.nearDoor);
          return;
        }
        if (e.code === "Escape") {
          if (hudUi.getState().settingsOpen) {
            hudUi.openSettings(false);
            return;
          }
          if (s.mode === "blueprint") blueprint.cancel();
          else if (escOpensSettings(s)) hudUi.openSettings(true);
        }
      }
      if (isLiveMode(s.mode) && MOVE_KEYS.has(e.code)) {
        if (e.code === "ControlLeft" && !e.ctrlKey) return;
        moveInput.keys.add(e.code);
        if (e.code === "Space") moveInput.jumpQueued = true;
        e.preventDefault();
      }
    };
    const up = (e: KeyboardEvent) => {
      moveInput.keys.delete(e.code);
    };
    const reset = () => moveInput.reset();
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", reset);
      moveInput.reset();
    };
  }, [store, nav]);
}

/** The on-screen joystick for touch devices (iOS has no pointer lock). */
function Joystick() {
  const t = useWorldT();
  const knob = useRef<HTMLDivElement>(null);
  const base = useRef<HTMLDivElement>(null);
  const active = useRef<number | null>(null);
  const RADIUS = 48;
  const move = (e: ReactPointerEvent) => {
    if (active.current !== e.pointerId || !base.current) return;
    const rect = base.current.getBoundingClientRect();
    let dx = e.clientX - (rect.left + rect.width / 2);
    let dy = e.clientY - (rect.top + rect.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > RADIUS) {
      dx = (dx / len) * RADIUS;
      dy = (dy / len) * RADIUS;
    }
    moveInput.joyX = dx / RADIUS;
    moveInput.joyY = -dy / RADIUS;
    if (knob.current) knob.current.style.transform = `translate(${dx}px, ${dy}px)`;
  };
  const end = (e: ReactPointerEvent) => {
    if (active.current !== e.pointerId) return;
    active.current = null;
    moveInput.joyX = 0;
    moveInput.joyY = 0;
    if (knob.current) knob.current.style.transform = "";
  };
  return (
    <div
      ref={base}
      className="world-joystick"
      data-testid="world-joystick"
      role="application"
      aria-label={t("nav.joystick")}
      onPointerDown={(e) => {
        active.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e);
      }}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      <div ref={knob} className="world-joystick-knob" />
    </div>
  );
}

/** A button that holds virtual keys while it is pressed (mouse or touch), for walking without WASD. */
function HoldButton({
  keys,
  turn,
  label,
  testId,
  children,
}: {
  keys?: readonly string[];
  turn?: -1 | 1;
  label: string;
  testId: string;
  children: string;
}) {
  const down = useRef<number | null>(null);
  const press = (on: boolean) => {
    for (const k of keys ?? []) {
      if (on) moveInput.keys.add(k);
      else moveInput.keys.delete(k);
    }
    if (turn !== undefined) moveInput.turn = on ? turn : 0;
  };
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      title={label}
      onPointerDown={(e) => {
        down.current = e.pointerId;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // Synthetic pointers cannot be captured.
        }
        press(true);
      }}
      onPointerUp={(e) => {
        if (down.current === e.pointerId) press(false);
        down.current = null;
      }}
      onPointerCancel={() => {
        press(false);
        down.current = null;
      }}
      onLostPointerCapture={() => press(false)}
    >
      {children}
    </button>
  );
}

/** On-screen walking for mouse users: hold to move, turn left and right, up and down when flying. */
function WalkPad({ fly, nav }: { fly: boolean; nav: NavStore }) {
  const t = useWorldT();
  const locked = useNavState(nav).locked;
  return (
    <div
      className="world-walkpad"
      role="group"
      aria-label={t("nav.pad")}
      data-testid="world-walkpad"
    >
      <HoldButton testId="walk-forward" keys={["KeyW"]} label={t("nav.forward")}>
        ▲
      </HoldButton>
      <HoldButton testId="walk-left" turn={-1} label={t("nav.turnLeft")}>
        ◀
      </HoldButton>
      <HoldButton testId="walk-back" keys={["KeyS"]} label={t("nav.back")}>
        ▼
      </HoldButton>
      <HoldButton testId="walk-right" turn={1} label={t("nav.turnRight")}>
        ▶
      </HoldButton>
      {fly ? (
        <>
          <HoldButton testId="walk-up" keys={["Space"]} label={t("nav.up")}>
            ⤒
          </HoldButton>
          <HoldButton testId="walk-down" keys={["KeyC"]} label={t("nav.down")}>
            ⤓
          </HoldButton>
        </>
      ) : null}
      <button
        type="button"
        className="world-walkpad-look"
        data-testid="world-mouselook"
        aria-pressed={locked}
        title={t("nav.mouseLookHint")}
        onClick={() => nav.requestLock()}
      >
        {t("nav.mouseLook")}
      </button>
    </div>
  );
}

/**
 * Everything the player sees on top of the 3D view: the camera mode switcher, the help line, the
 * door prompt, the elevator and exit while inside, the blueprint undo chip, toasts, the touch
 * joystick, the fade between outside and inside and the dev stats overlay.
 */
export function NavHud({ store, nav = navStore }: { store: WorldStore; nav?: NavStore }) {
  const t = useWorldT();
  const s = useNavState(nav);
  const world = useWorldState(store);
  const bp = useSyncExternalStore(blueprint.subscribe, blueprint.getState);
  useNavKeys(store, nav);
  useEffect(() => {
    void nav.loadCapabilities().then(() => nav.loadPrefs());
  }, [nav]);
  useEffect(() => {
    if (import.meta.env["VITE_UZ_MOCK"] !== "1") return;
    // Test hook, mock mode only: e2e reads where the player stands and drives walking directly.
    const hook = {
      state: () => {
        const { mode, inside, cutaway, nearDoor, fade, locked } = nav.getState();
        return { mode, inside, cutaway, nearDoor, fade, locked };
      },
      walker: () => ({ ...nav.walker }),
      blueprint: () => blueprint.getState(),
      teleport: (x: number, z: number, yaw = 0, pitch = 0) => {
        nav.walker = { ...nav.walker, x, z, yaw, pitch, y: 0, vy: 0 };
      },
      /** Is this building's door open to the visitor yet (the org tree loads after the scene)? */
      canEnter: (id: string) => nav.entryOf(id).ok,
      enter: (id: string) => nav.enter(id),
      exit: () => nav.exit(),
      autoTier: (tier: "low" | "medium" | "high") => nav.setAutoTier(tier),
      /** The player's locomotion: position, body and camera yaw, speed, gait, graph state. */
      loco: () => ({ ...locoStatus }),
      /** Holds aim (as the right mouse button does). */
      aim: (on: boolean) => {
        moveInput.aim = on;
      },
      key: (code: string, down: boolean) => {
        if (down) moveInput.keys.add(code);
        else moveInput.keys.delete(code);
      },
    };
    (globalThis as { __uzNav?: typeof hook }).__uzNav = hook;
    return () => {
      delete (globalThis as { __uzNav?: typeof hook }).__uzNav;
    };
  }, [nav]);

  const helpIdle = useIdle(hudIdleMs("help"));
  const controlsIdle = useIdle(hudIdleMs("controls"));
  const { settingsOpen, prefs } = useHudUi();
  const driving = useSyncExternalStore(
    vehicleStore.subscribe,
    () => vehicleStore.getState().drivingId !== null,
  );
  const modes = nav.modes();
  const live = isLiveMode(s.mode);
  const coarse = useMemo(() => isCoarseDevice(), []);

  const nearNode = s.nearDoor ? store.node(s.nearDoor) : undefined;
  const selected = world.selected ? store.node(world.selected.id) : undefined;
  const canEnterSelected =
    !s.inside &&
    selected !== undefined &&
    (s.mode === "orbit" || s.mode === "blueprint") &&
    selected.geometry.kind === "procedural" &&
    selected.geometry.generator === "building.box";
  const name = (n: { label?: string | undefined } | undefined, landmark = false) =>
    landmark ? t("panel.landmarkTitle") : (n?.label ?? t("target.unknown"));

  return (
    <div
      className={`world-hud${s.locked ? " is-locked" : ""}`}
      data-testid="world-hud"
      data-mode={s.mode}
      data-idle={String(controlsIdle && !settingsOpen)}
    >
      <PerfOverlay />

      <GameCorner />
      {prefs.compass ? <Compass store={store} nav={nav} /> : null}
      {prefs.minimap ? <Minimap store={store} nav={nav} /> : null}
      {s.mode === "first_person" ? <Crosshair /> : null}

      <div
        className={`world-controls${settingsOpen ? " is-sheet" : ""}`}
        data-testid="world-controls"
      >
        {driving ? null : (
          <div
            className={`hud-modes${prefs.modeLabels ? " has-labels" : ""}`}
            role="group"
            aria-label={t("modes.label")}
          >
            {modes.map((m: CameraMode, i) => (
              <button
                key={m}
                type="button"
                aria-pressed={m === s.mode}
                aria-label={t(`modes.${m}`)}
                title={`${t(`modes.${m}`)} (${i + 1})`}
                data-testid={`mode-${m}`}
                onClick={() => nav.setMode(m)}
              >
                <Icon name={ICONS[m]} size={16} />
                <span className="hud-mode-label">{t(`modes.${m}`)}</span>
                <kbd>{i + 1}</kbd>
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          className="hud-btn hud-gear"
          data-testid="world-controls-toggle"
          aria-expanded={settingsOpen}
          aria-haspopup="dialog"
          aria-label={t("game.settings")}
          title={t("game.settings")}
          onClick={() => hudUi.toggleSettings()}
        >
          <Icon name="settings" size={18} />
        </button>
      </div>
      <SettingsSheet nav={nav} />

      <FloorNav store={store} nav={nav} />

      <p
        className="world-help"
        data-testid="world-help"
        data-hidden={String(helpIdle && !s.toasts.length)}
      >
        {s.locked ? t("nav.lockedHint") : t(`help.${s.mode}`)}{" "}
        {live && coarse ? t("help.touch") : ""}
      </p>

      <div className="world-hud-stack">
        {s.toasts.length > 0 ? (
          <ul className="world-toasts" aria-live="polite" aria-label={t("nav.toasts")}>
            {s.toasts.map((toast) => (
              <li
                key={toast.id}
                className={`world-toast is-${toast.kind}`}
                data-testid="world-toast"
              >
                <span>{String(t(toast.key, { ...toast.params }))}</span>
                <button
                  type="button"
                  aria-label={t("nav.dismiss")}
                  onClick={() => nav.dismiss(toast.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {bp.chip ? (
          <div className="world-chip" data-testid="world-undo-chip">
            <span>{t("blueprint.moved", { name: bp.chip.label || t("target.unknown") })}</span>
            <button type="button" onClick={() => void blueprint.undo()}>
              {t("blueprint.undo")}
            </button>
          </div>
        ) : null}

        {nearNode && !s.inside ? (
          <button
            type="button"
            className="hud-prompt"
            data-testid="world-enter"
            onClick={() => void nav.enter(nearNode.id)}
          >
            {t("nav.enter", { name: name(nearNode, isLandmark(nearNode)) })} <kbd>E</kbd>
          </button>
        ) : canEnterSelected ? (
          <button
            type="button"
            className="hud-prompt"
            data-testid="world-enter-selected"
            onClick={() => void nav.enter(selected.id)}
          >
            {t("nav.enter", { name: name(selected, isLandmark(selected)) })}
          </button>
        ) : null}

        {s.inside ? <ElevatorHud store={store} nav={nav} /> : null}

        {s.mode === "blueprint" ? (
          <button
            type="button"
            className="hud-prompt"
            data-testid="blueprint-rotate"
            title={t("blueprint.rotateHint")}
            disabled={!bp.drag && !(selected && selected.parentId === null)}
            onClick={() => blueprint.rotate(world.selected?.id ?? null)}
          >
            ↻ {t("blueprint.rotate")} <kbd>R</kbd>
          </button>
        ) : null}
      </div>

      {live && coarse ? <Joystick /> : null}
      {live && coarse && s.mode !== "fly" ? (
        <button
          type="button"
          className="world-jump"
          data-testid="world-jump"
          aria-label={t("nav.jump")}
          onPointerDown={(e) => {
            e.preventDefault();
            moveInput.jumpQueued = true;
          }}
        >
          <span aria-hidden="true">⤒</span>
        </button>
      ) : null}
      {live ? <WalkPad fly={s.mode === "fly"} nav={nav} /> : null}
      <div
        className="world-fade"
        data-testid="world-fade"
        style={{ opacity: s.fade }}
        aria-hidden="true"
      />
    </div>
  );
}
